import { createEntityAdapter, createReducer, EntityState, isAnyOf } from '@reduxjs/toolkit';

import isEqual from 'lodash/isEqual';

import { productsApi } from '../../api/product';
import { Product, ProductFilterValues, ProductsSortBy } from '../../api/product/types';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { logoutThunk } from '../user';
import {
  clearProductsList,
  setAddPriceListLoading,
  setDeletePriceListMode,
  setImportProductsLoading,
  setLastAddedProductId,
  setPriceListActionsDisabled,
  setProductsByCategoryKeyword,
  setProductsFilterBy,
  setProductsKeyword,
  setProductsPage,
  setProductsSortBy,
} from './products.actions';

export const productsAdapter = createEntityAdapter<Product>();

interface State {
  list: EntityState<Product>;
  sortBy?: ProductsSortBy;
  filterBy?: ProductFilterValues;
  keyword?: string;
  currentPage: number;
  resultsCount: number;
  lastAddedToCartId?: number;
  isDeletePriceListMode: boolean;
  isPriceListActionsDisabled: boolean;
  importLoading: boolean;
  addPriceListLoading: boolean;
}

const initialState: State = {
  sortBy: undefined,
  filterBy: undefined,
  keyword: undefined,
  list: productsAdapter.getInitialState(),
  currentPage: 1,
  resultsCount: 0,
  lastAddedToCartId: undefined,
  isDeletePriceListMode: false,
  isPriceListActionsDisabled: false,
  importLoading: false,
  addPriceListLoading: false,
};

export const productsReducer = createReducer(initialState, (builder) => {
  builder.addCase(logoutThunk.fulfilled, () => {
    return { ...initialState, filterBy: undefined };
  });
  builder.addCase(setProductsSortBy, (state, { payload }) => {
    if (isEqual(state.sortBy, payload)) {
      return;
    }
    state.sortBy = payload;
    state.currentPage = 1;
    state.list = productsAdapter.getInitialState();
  });
  builder.addCase(setProductsFilterBy, (state, { payload }) => {
    if (isEqual(state.filterBy, payload)) {
      return;
    }
    if (state.filterBy?.supplier_id !== payload?.supplier_id && state.sortBy && state.sortBy.length === 1) {
      state.sortBy = LocalStorageService.getItem('last_product_sort') || undefined;
    }
    state.filterBy = payload;
    state.currentPage = 1;
    state.list = productsAdapter.getInitialState();
  });
  builder.addCase(setProductsByCategoryKeyword, (state, { payload }) => {
    if (state.keyword === payload.keyword) {
      return;
    }
    state.sortBy = payload.keyword ? ['relevance'] : LocalStorageService.getItem('last_product_sort') || undefined;
    state.keyword = payload.keyword;
    state.currentPage = 1;
    state.list = productsAdapter.getInitialState();
  });
  builder.addCase(setProductsKeyword, (state, { payload }) => {
    if (state.keyword === payload.keyword) {
      return;
    }
    state.keyword = payload.keyword;
    state.currentPage = 1;
  });
  builder.addCase(setProductsPage, (state, { payload }) => {
    state.currentPage = payload;
  });
  builder.addCase(setLastAddedProductId, (state, { payload }) => {
    state.lastAddedToCartId = payload;
  });
  builder.addCase(setDeletePriceListMode, (state, { payload }) => {
    state.isDeletePriceListMode = payload;
  });
  builder.addCase(setPriceListActionsDisabled, (state, { payload }) => {
    state.isPriceListActionsDisabled = payload;
  });
  builder.addCase(setImportProductsLoading, (state, { payload }) => {
    state.importLoading = payload;
  });
  builder.addCase(setAddPriceListLoading, (state, { payload }) => {
    state.addPriceListLoading = payload;
  });
  builder.addCase(clearProductsList, (state) => {
    state.list = productsAdapter.getInitialState();
    state.keyword = undefined;
    state.currentPage = 1;
    state.resultsCount = 0;
  });
  builder.addMatcher(
    isAnyOf(productsApi.endpoints.getProductsByCategory.matchFulfilled, productsApi.endpoints.getProductsBySupplier.matchFulfilled),
    (state, action) => {
      if (
        isEqual(action.meta.arg.originalArgs!.filter, state.filterBy) &&
        isEqual(action.meta.arg.originalArgs!.sort, state.sortBy) &&
        action.meta.arg.originalArgs!.keyword === state.keyword &&
        action.meta.arg.originalArgs!.currentPage === state.currentPage
      ) {
        state.list = productsAdapter.setMany(state.list, action.payload.data.products);
        state.resultsCount = action.payload.products_result;
      }
    },
  );
  builder.addMatcher(
    isAnyOf(productsApi.endpoints.unFavoriteProduct.matchFulfilled, productsApi.endpoints.favoriteProduct.matchFulfilled),
    (state, { payload }) => {
      if (state.filterBy?.favourites) {
        state.list = productsAdapter.removeOne(state.list, payload.product.id);
        state.resultsCount = state.resultsCount - 1;
      } else {
        state.list = productsAdapter.upsertOne(state.list, payload.product);
      }
    },
  );
});
